<script>
import { userService } from '@/shared/services/user.service';
import notificationService from '@/shared/services/notification.service';

/** Les champs que l'import sait remplir, dans l'ordre du modèle. */
const FIELDS = [
  { key: 'lastName', label: 'Nom', required: true },
  { key: 'firstName', label: 'Prénom', required: true },
  { key: 'email', label: 'Email', required: true },
  { key: 'phone', label: 'Téléphone', required: false },
  { key: 'role', label: 'Rôle', required: true },
  { key: 'code', label: 'Code enquêteur', required: false },
];

/**
 * Import d'utilisateurs en trois temps : ce qu'il faut préparer, le fichier,
 * puis la correspondance des colonnes.
 *
 * Le fichier n'est pas lu ici : il part au serveur, qui le comprend et le
 * valide. Un seul lecteur, donc une seule façon de se tromper — et la même
 * règle qu'à la création à l'unité sur les rôles qu'on a le droit d'attribuer.
 */
export default {
  name: 'ImportUsersModal',
  props: {
    modelValue: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'imported'],
  data() {
    return {
      step: 1,
      fields: FIELDS,
      file: null,
      inspecting: false,
      importing: false,
      error: '',
      columns: [],
      rows: [],
      mapping: {},
      errors: [],
      downloadingReport: false,
      roles: [],
      outcome: null,
    };
  },
  computed: {
    /** Les étapes, dans l'ordre, telles que l'indicateur les montre. */
    steps() {
      return [
        { n: 1, label: 'Préparer' },
        { n: 2, label: 'Fichier' },
        { n: 3, label: 'Colonnes' },
      ];
    },

    isVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },

    preview() {
      return this.rows.slice(0, 5);
    },

    /** Un champ obligatoire sans colonne empêche d'aller plus loin. */
    mappingComplete() {
      return this.fields
        .filter((f) => f.required)
        .every((f) => typeof this.mapping[f.key] === 'number');
    },
  },
  methods: {
    reset() {
      Object.assign(this, {
        step: 1,
        file: null,
        inspecting: false,
        importing: false,
        error: '',
        columns: [],
        rows: [],
        mapping: {},
        errors: [],
        outcome: null,
      });
    },

    /**
     * Revenir en arrière est libre; avancer passe par « Suivant », qui est le
     * seul chemin où le fichier est lu et la correspondance établie.
     */
    goTo(step) {
      if (step < this.step) {
        this.step = step;
        this.error = '';
      }
    },

    /**
     * Les rôles que celui qui importe a le droit d'attribuer. Ils viennent du
     * serveur: ce sont des lignes en base, et c'est lui qui tranche.
     */
    async loadOptions() {
      try {
        const options = await userService.getImportOptions();
        this.roles = options.roles ?? [];
      } catch {
        // Sans la liste, l'étape 1 reste utilisable: le serveur redira la
        // règle au moment de l'import, ligne par ligne.
        this.roles = [];
      }
    },

    onHidden() {
      this.reset();
      this.isVisible = false;
    },

    messageOf(error, fallback) {
      const payload = error?.response?.data?.message;

      if (Array.isArray(payload)) {
        return payload.join(' ');
      }

      if (typeof payload === 'string' && payload.trim()) {
        return payload;
      }

      // Une requete partie sans reponse: le serveur est injoignable. Une erreur
      // sans requete du tout n'est pas un probleme de reseau - annoncer
      // « serveur injoignable » enverrait chercher au mauvais endroit.
      if (!error?.response && error?.request) {
        return 'Serveur injoignable. Veuillez réessayer.';
      }

      return fallback;
    },

    save(blob, filename) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },

    async downloadTemplate() {
      try {
        this.save(
          await userService.getImportTemplate(),
          'modele-import-utilisateurs.xlsx',
        );
      } catch (error) {
        this.error = this.messageOf(
          error,
          'Le modèle n’a pas pu être téléchargé.',
        );
      }
    },

    onFilePicked(event) {
      this.file = event.target.files?.[0] ?? null;
      this.error = '';
    },

    /** Étape 2 → 3 : le serveur lit le fichier et propose une correspondance. */
    async inspectFile() {
      if (!this.file) {
        return;
      }

      this.inspecting = true;
      this.error = '';

      try {
        const result = await userService.inspectImport(this.file);

        this.columns = result.columns;
        this.rows = result.rows;
        this.mapping = { ...result.suggestedMapping };
        this.errors = [];
        this.step = 3;
      } catch (error) {
        this.error = this.messageOf(error, 'Le fichier n’a pas pu être lu.');
      } finally {
        this.inspecting = false;
      }
    },

    async submitImport() {
      this.importing = true;
      this.error = '';
      this.errors = [];

      try {
        const outcome = await userService.importUsers({
          mapping: this.mapping,
          columns: this.columns,
          rows: this.rows,
        });

        if (outcome.errors?.length) {
          // Rien n'a été créé: le fichier revient corrigé, ou pas du tout.
          this.errors = outcome.errors;
          return;
        }

        this.$emit('imported');

        // Une invitation qui n'est pas partie laisse un compte que personne
        // n'attend: cela se lit, cela ne se signale pas en passant.
        if (outcome.uninvited?.length) {
          this.outcome = outcome;
          return;
        }

        notificationService.toastSuccess(
          `${outcome.created} utilisateur${outcome.created > 1 ? 's' : ''} importé${
            outcome.created > 1 ? 's' : ''
          }, invitation${outcome.created > 1 ? 's' : ''} envoyée${
            outcome.created > 1 ? 's' : ''
          }.`,
        );
        this.onHidden();
      } catch (error) {
        this.error = this.messageOf(error, 'L’import a échoué.');
      } finally {
        this.importing = false;
      }
    },

    async downloadReport() {
      this.downloadingReport = true;

      try {
        this.save(
          await userService.getImportReport(this.errors),
          'erreurs-import-utilisateurs.xlsx',
        );
      } catch (error) {
        this.error = this.messageOf(
          error,
          'Le rapport n’a pas pu être téléchargé.',
        );
      } finally {
        this.downloadingReport = false;
      }
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(open) {
        if (open && !this.roles.length) {
          this.loadOptions();
        }
      },
    },
  },
};
</script>

<template>
  <BModal
    v-model="isVisible"
    modal-class="zoomIn"
    hide-footer
    no-close-on-backdrop
    centered
    size="lg"
    title="Importer des utilisateurs"
    @hidden="onHidden"
  >
    <ol class="import-steps">
      <li
        v-for="s in steps"
        :key="s.n"
        class="import-steps__item"
        :class="{
          'import-steps__item--active': step === s.n,
          'import-steps__item--done': step > s.n,
        }"
      >
        <button
          type="button"
          class="import-steps__btn"
          :disabled="s.n >= step"
          :aria-current="step === s.n ? 'step' : null"
          @click="goTo(s.n)"
        >
          <span class="import-steps__badge">
            <i v-if="step > s.n" class="ri-check-line"></i>
            <template v-else>{{ s.n }}</template>
          </span>
          <span class="import-steps__label">{{ s.label }}</span>
        </button>
      </li>
    </ol>

    <!-- Étape 1 — ce qu'il faut préparer -->
    <section v-if="step === 1" class="import-step">
      <p class="import-step__lead">
        Importez plusieurs comptes d'un coup à partir d'un fichier Excel
        (<code>.xlsx</code>) ou CSV.
      </p>
      <ul class="import-step__list">
        <li>La première ligne doit porter les noms de colonnes.</li>
        <li>
          Colonnes obligatoires : <strong>Nom</strong>, <strong>Prénom</strong>,
          <strong>Email</strong> et <strong>Rôle</strong>. Téléphone et code
          enquêteur sont facultatifs.
        </li>
        <li v-if="roles.length">
          Valeurs acceptées pour <strong>Rôle</strong> :
          <strong>{{ roles.join(', ') }}</strong
          >. Le modèle les propose en liste déroulante.
        </li>
        <li>
          Un email déjà utilisé, ou répété dans le fichier, sera signalé sans
          rien créer.
        </li>
        <li>
          Chaque compte importé reçoit le même e-mail d'activation qu'à la
          création à l'unité, valable 7 jours : aucun mot de passe n'est à
          fournir, ni dans le fichier ni ailleurs.
        </li>
        <li>1000 lignes au maximum par fichier.</li>
      </ul>

      <button type="button" class="import-link" @click="downloadTemplate">
        <i class="ri-download-2-line"></i>
        Télécharger le modèle
      </button>
    </section>

    <!-- Étape 2 — le fichier -->
    <section v-else-if="step === 2" class="import-step">
      <label class="import-step__label" for="import-file">
        Fichier à importer
      </label>
      <input
        id="import-file"
        type="file"
        class="form-control"
        accept=".xlsx,.csv"
        @change="onFilePicked"
      />
      <p v-if="file" class="import-step__hint">{{ file.name }}</p>
    </section>

    <!-- Étape 3 — la correspondance -->
    <section v-else class="import-step">
      <p class="import-step__lead">
        Indiquez quelle colonne du fichier alimente quel champ.
        {{ rows.length }} ligne{{ rows.length > 1 ? 's' : '' }} lue{{
          rows.length > 1 ? 's' : ''
        }}.
      </p>

      <div class="import-map">
        <label v-for="field in fields" :key="field.key" class="import-map__row">
          <span class="import-map__label">
            {{ field.label }}
            <span v-if="field.required" class="required-star">*</span>
          </span>
          <select v-model="mapping[field.key]" class="form-select">
            <option :value="undefined">— aucune —</option>
            <option
              v-for="(column, index) in columns"
              :key="index"
              :value="index"
            >
              {{ column || `Colonne ${index + 1}` }}
            </option>
          </select>
        </label>
      </div>

      <details class="import-preview">
        <summary>Aperçu des premières lignes</summary>
        <div class="import-preview__wrap">
          <table class="import-preview__table">
            <thead>
              <tr>
                <th v-for="(column, index) in columns" :key="index">
                  {{ column || `Colonne ${index + 1}` }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(line, index) in preview" :key="index">
                <td v-for="(cell, cellIndex) in line" :key="cellIndex">
                  {{ cell }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </details>

      <!-- Des comptes créés, mais des invitations restées à quai -->
      <div v-if="outcome" class="import-outcome">
        <p class="import-outcome__lead">
          {{ outcome.created }} compte{{
            outcome.created > 1 ? 's' : ''
          }}
          créé{{ outcome.created > 1 ? 's' : '' }}, mais
          {{ outcome.uninvited.length }} invitation{{
            outcome.uninvited.length > 1 ? 's' : ''
          }}
          n'{{ outcome.uninvited.length > 1 ? 'ont' : 'a' }} pas pu être
          envoyée{{ outcome.uninvited.length > 1 ? 's' : '' }}.
        </p>
        <ul class="import-outcome__list">
          <li v-for="email in outcome.uninvited.slice(0, 5)" :key="email">
            {{ email }}
          </li>
        </ul>
        <p v-if="outcome.uninvited.length > 5" class="import-outcome__more">
          … et {{ outcome.uninvited.length - 5 }} autre{{
            outcome.uninvited.length - 5 > 1 ? 's' : ''
          }}.
        </p>
        <p class="import-outcome__hint">
          Ces comptes existent : leurs titulaires peuvent définir leur mot de
          passe depuis « Mot de passe oublié ».
        </p>
      </div>

      <!-- Le rapport : ligne, colonne, problème -->
      <div v-if="errors.length" class="import-errors">
        <p class="import-errors__lead">
          {{ errors.length }} problème{{ errors.length > 1 ? 's' : '' }} — aucun
          compte n'a été créé.
        </p>
        <ul class="import-errors__list">
          <li v-for="(item, index) in errors.slice(0, 5)" :key="index">
            Ligne {{ item.row }}, colonne « {{ item.column }} » :
            {{ item.message }}
          </li>
        </ul>
        <p v-if="errors.length > 5" class="import-errors__more">
          … et {{ errors.length - 5 }} autre{{
            errors.length - 5 > 1 ? 's' : ''
          }}.
        </p>
        <button
          type="button"
          class="import-link"
          :disabled="downloadingReport"
          @click="downloadReport"
        >
          <i class="ri-download-2-line"></i>
          Télécharger le rapport d'erreurs
        </button>
      </div>
    </section>

    <p v-if="error" class="import-error" role="alert">{{ error }}</p>

    <footer class="import-footer">
      <button
        v-if="outcome"
        type="button"
        class="import-btn import-btn--primary"
        @click="onHidden"
      >
        Fermer
      </button>
      <template v-else>
        <button
          v-if="step > 1"
          type="button"
          class="import-btn import-btn--ghost"
          @click="goTo(step - 1)"
        >
          Précédent
        </button>
        <button
          v-if="step === 1"
          type="button"
          class="import-btn import-btn--primary"
          @click="step = 2"
        >
          Suivant
        </button>
        <button
          v-else-if="step === 2"
          type="button"
          class="import-btn import-btn--primary"
          :disabled="!file || inspecting"
          @click="inspectFile"
        >
          {{ inspecting ? 'Lecture…' : 'Suivant' }}
        </button>
        <button
          v-else
          type="button"
          class="import-btn import-btn--primary"
          :disabled="!mappingComplete || importing"
          @click="submitImport"
        >
          {{ importing ? 'Import…' : 'Importer' }}
        </button>
      </template>
    </footer>
  </BModal>
</template>

<style scoped>
.import-steps {
  display: flex;
  align-items: center;
  list-style: none;
  margin: 4px 0 20px;
  padding: 0 0 18px;
  border-bottom: 1px solid #eef0f4;
}

/* Le trait qui relie les étapes part de chaque pastille, sauf la première. */
.import-steps__item {
  display: flex;
  align-items: center;
  /* La première étape se serre sur son libellé : si elle s'étirait, le vide
     resterait derrière elle au lieu de devenir le trait de liaison. */
  flex: 0 0 auto;
}

.import-steps__item + .import-steps__item {
  flex: 1;
}

.import-steps__item + .import-steps__item::before {
  content: '';
  flex: 1;
  height: 2px;
  margin: 0 8px;
  border-radius: 2px;
  background: #e5e9ef;
}

.import-steps__item--active::before,
.import-steps__item--done::before {
  background: #005bd0;
}

.import-steps__btn {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: none;
  padding: 0;
  font-size: 13px;
  color: #667085;
  white-space: nowrap;
}

/* Revenir en arrière se clique; l'étape en cours et celles à venir, non. */
.import-steps__btn:disabled {
  cursor: default;
}

.import-steps__item--done .import-steps__btn {
  color: #005bd0;
  cursor: pointer;
}

.import-steps__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid #e5e9ef;
  border-radius: 50%;
  background: #fff;
  font-size: 12px;
  font-weight: 600;
  color: #98a2b3;
}

.import-steps__item--active .import-steps__badge {
  border-color: #005bd0;
  background: #005bd0;
  color: #fff;
}

.import-steps__item--done .import-steps__badge {
  border-color: #005bd0;
  background: #eef4ff;
  color: #005bd0;
}

.import-steps__item--active .import-steps__label {
  color: #101828;
  font-weight: 600;
}

.import-outcome {
  padding: 12px 14px;
  border: 1px solid #f5d9a8;
  border-radius: 8px;
  background: #fff8ea;
}

.import-outcome__lead {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: #b54708;
}

.import-outcome__list {
  margin: 0 0 8px;
  padding-left: 18px;
  font-size: 12px;
  color: #7a4a12;
}

.import-outcome__more {
  margin: 0 0 8px;
  font-size: 12px;
  color: #7a4a12;
}

.import-outcome__hint {
  margin: 0;
  font-size: 12px;
  color: #7a4a12;
}

.import-step__lead {
  font-size: 13px;
  color: #475467;
}

.import-step__list {
  margin: 0 0 16px;
  padding-left: 18px;
  font-size: 13px;
  color: #475467;
}

.import-step__list li {
  margin-bottom: 4px;
}

.import-step__label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #475467;
}

.import-step__hint {
  margin-top: 8px;
  font-size: 12px;
  color: #667085;
}

.import-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: none;
  padding: 0;
  color: #005bd0;
  font-size: 13px;
  cursor: pointer;
}

.import-map {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
  margin-bottom: 16px;
}

.import-map__row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.import-map__label {
  font-size: 12px;
  font-weight: 500;
  color: #475467;
}

.import-map .form-select {
  height: 36px;
  padding: 6px 10px;
  font-size: 13px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
}

.import-preview {
  margin-bottom: 12px;
  font-size: 12px;
  color: #475467;
}

.import-preview__wrap {
  overflow-x: auto;
  margin-top: 8px;
}

.import-preview__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.import-preview__table th,
.import-preview__table td {
  padding: 6px 10px;
  border: 1px solid #e5e9ef;
  text-align: left;
  white-space: nowrap;
}

.import-errors {
  padding: 12px 14px;
  border: 1px solid #f5c2c7;
  border-radius: 8px;
  background: #fdeaec;
}

.import-errors__lead {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: #dc3545;
}

.import-errors__list {
  margin: 0 0 8px;
  padding-left: 18px;
  font-size: 12px;
  color: #8a1f2a;
}

.import-errors__more {
  margin: 0 0 8px;
  font-size: 12px;
  color: #8a1f2a;
}

.import-error {
  margin: 12px 0 0;
  font-size: 12px;
  color: #dc3545;
}

.import-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}

.import-btn {
  padding: 9px 18px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.import-btn--ghost {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #101828;
}

.import-btn--primary {
  border: 0;
  background: #005bd0;
  color: #fff;
}

.import-btn--primary:disabled {
  opacity: 0.55;
  cursor: default;
}

@media (max-width: 575px) {
  .import-map {
    grid-template-columns: 1fr;
  }
}
</style>
